import React from 'react';
import { Alert, Modal, Typography, Space, Spin } from 'antd';
import { ClockCircleOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { MaintenanceStatus } from '../services/maintenance.service';

const { Title, Text, Paragraph } = Typography;

interface MaintenanceOverlayProps {
  status: MaintenanceStatus;
  onClose?: () => void;
}

const MaintenanceOverlay: React.FC<MaintenanceOverlayProps> = ({ status, onClose }) => {
  const { maintenance } = status;

  if (!maintenance.active) {
    return null;
  }

  const getIcon = () => {
    switch (maintenance.mode) {
      case 'FULL':
        return <WarningOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />;
      case 'READ_ONLY':
        return <InfoCircleOutlined style={{ fontSize: 48, color: '#F59E0B' }} />;
      case 'SURVEY_ONLY':
        return <ClockCircleOutlined style={{ fontSize: 48, color: '#faad14' }} />;
      default:
        return <Spin size="large" />;
    }
  };

  const getTitle = () => {
    switch (maintenance.mode) {
      case 'FULL':
        return 'System Maintenance';
      case 'READ_ONLY':
        return 'Read-Only Mode';
      case 'SURVEY_ONLY':
        return 'Limited Access';
      default:
        return 'Maintenance Mode';
    }
  };

  const getDescription = () => {
    if (maintenance.message) {
      return maintenance.message;
    }
    
    switch (maintenance.mode) {
      case 'FULL':
        return 'The system is currently undergoing maintenance. Please try again later.';
      case 'READ_ONLY':
        return 'The system is in read-only mode. You can view data but cannot make changes.';
      case 'SURVEY_ONLY':
        return 'Only survey functionality is available at this time.';
      default:
        return 'The system is temporarily unavailable.';
    }
  };

  const formatEndTime = (isoTime: string) => {
    const date = new Date(isoTime);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 60) {
      return `in approximately ${diffMins} minutes`;
    } else if (diffMins < 1440) {
      const hours = Math.round(diffMins / 60);
      return `in approximately ${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      return date.toLocaleString();
    }
  };

  // For FULL maintenance, show a modal
  if (maintenance.mode === 'FULL') {
    return (
      <Modal
        open={true}
        title={null}
        footer={null}
        closable={false}
        centered
        maskClosable={false}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          {getIcon()}
          <Title level={3} style={{ marginTop: 24 }}>
            {getTitle()}
          </Title>
          <Paragraph style={{ fontSize: 16, marginTop: 16 }}>
            {getDescription()}
          </Paragraph>
          {maintenance.expectedEndTime && (
            <Text type="secondary">
              Expected to be available {formatEndTime(maintenance.expectedEndTime)}
            </Text>
          )}
        </div>
      </Modal>
    );
  }

  // For other modes, show an alert banner
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: '12px',
      background: maintenance.mode === 'READ_ONLY' ? '#e6f7ff' : '#fff7e6',
      borderBottom: '1px solid',
      borderColor: maintenance.mode === 'READ_ONLY' ? '#91d5ff' : '#ffd591',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <Alert
        message={
          <Space>
            {maintenance.mode === 'READ_ONLY' ? (
              <InfoCircleOutlined />
            ) : (
              <ClockCircleOutlined />
            )}
            <strong>{getTitle()}</strong>
          </Space>
        }
        description={
          <Space direction="vertical" size={4}>
            <Text>{getDescription()}</Text>
            {maintenance.expectedEndTime && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Normal service will resume {formatEndTime(maintenance.expectedEndTime)}
              </Text>
            )}
          </Space>
        }
        type={maintenance.mode === 'READ_ONLY' ? 'info' : 'warning'}
        showIcon={false}
        closable={!!onClose}
        onClose={onClose}
      />
    </div>
  );
};

export default MaintenanceOverlay;